import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Loader2, ArrowLeft, Plus, ChevronUp, ChevronDown, Eye, EyeOff, Copy, Trash2, Save, Globe,
  History, X, Monitor, Tablet, Smartphone, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, btnPrimary, btnSecondary, StatusBadge } from "@/components/admin/ui";
import { BlockRenderer, BLOCK_LABELS } from "@/components/cms/BlockRenderer";
import MediaPicker from "@/components/cms/MediaPicker";
import { mediaSrc } from "@/lib/api";

const SCHEMA = {
  hero: [["eyebrow", "text"], ["heading", "text"], ["subheading", "textarea"], ["image", "image"], ["ctaLabel", "text"], ["ctaHref", "url"]],
  text: [["heading", "text"], ["body", "textarea"]],
  image: [["image", "image"], ["caption", "text"]],
  image_text: [["heading", "text"], ["body", "textarea"], ["image", "image"], ["imageRight", "bool"]],
  cards: [["heading", "text"], ["items", "list:title,body,image"]],
  cta: [["heading", "text"], ["body", "textarea"], ["ctaLabel", "text"], ["ctaHref", "url"]],
  faq: [["heading", "text"], ["items", "list:q,a"]],
  gallery: [["images", "strlist"]],
};
const uid = () => Math.random().toString(36).slice(2, 9);
const inputCls = "w-full border border-[#1F2A22]/20 px-3 py-2 mt-1 bg-white text-sm text-[#1F2A22]";

function Field({ type, value, onChange }) {
  const [pick, setPick] = useState(false);
  if (type === "textarea") return <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className={inputCls} />;
  if (type === "bool") return <label className="flex items-center gap-2 mt-1 text-sm"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} /> Yes</label>;
  if (type === "image") return (
    <div>
      <div className="flex gap-2 mt-1">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} placeholder="https://… or choose from library" />
        <button type="button" onClick={() => setPick(true)} className="shrink-0 text-xs border border-[#1F2A22]/25 px-2.5 hover:bg-[#1F2A22] hover:text-white" data-testid="cms-image-library-btn">Library</button>
      </div>
      {value && <img src={mediaSrc(value)} alt="" className="h-16 mt-1 object-cover border border-[#1F2A22]/10" />}
      <MediaPicker open={pick} kind="image" onClose={() => setPick(false)} onSelect={(url) => onChange(url)} />
    </div>
  );
  return <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} placeholder={type === "url" ? "https://…" : ""} />;
}

function BlockFields({ block, update }) {
  const schema = SCHEMA[block.type] || [];
  const [galKey, setGalKey] = useState(null);
  const setProp = (k, v) => update({ ...block, props: { ...block.props, [k]: v } });
  return (
    <div className="space-y-3 mt-3">
      {schema.map(([key, type]) => {
        if (type.startsWith("list:")) {
          const subs = type.slice(5).split(",");
          const items = block.props?.[key] || [];
          const setItems = (arr) => setProp(key, arr);
          return (
            <div key={key}>
              <span className="text-xs text-[#5C6B61] uppercase">{key}</span>
              {items.map((it, i) => (
                <div key={i} className="border border-[#1F2A22]/12 p-2 mt-2 bg-[#F9F8F6]">
                  {subs.map((s) => (
                    <div key={s} className="mb-1"><span className="text-[10px] text-[#5C6B61]">{s}</span>
                      <Field type={s === "body" || s === "a" ? "textarea" : (s === "image" ? "image" : "text")} value={it[s]} onChange={(v) => setItems(items.map((x, j) => j === i ? { ...x, [s]: v } : x))} /></div>
                  ))}
                  <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-xs text-red-600 mt-1">Remove item</button>
                </div>
              ))}
              <button onClick={() => setItems([...items, {}])} className="text-xs text-[#FF6A00] mt-2 font-semibold">+ Add item</button>
            </div>
          );
        }
        if (type === "strlist") {
          const items = block.props?.[key] || [];
          return (
            <div key={key} data-testid={`cms-strlist-${key}`}>
              <span className="text-xs text-[#5C6B61] uppercase">{key} (images)</span>
              {items.map((v, i) => (
                <div key={i} className="flex items-center gap-2 mt-1">
                  {v && <img src={mediaSrc(v)} alt="" className="h-9 w-9 object-cover border border-[#1F2A22]/10 shrink-0" />}
                  <input value={v} onChange={(e) => setProp(key, items.map((x, j) => j === i ? e.target.value : x))} className={inputCls} placeholder="https://… or use library" />
                  <button onClick={() => setProp(key, items.filter((_, j) => j !== i))} className="text-red-600 px-2 shrink-0" title="Remove">×</button>
                </div>
              ))}
              <div className="flex gap-3 mt-2">
                <button onClick={() => setProp(key, [...items, ""])} className="text-xs text-[#FF6A00] font-semibold">+ Add URL</button>
                <button onClick={() => setGalKey(key)} className="text-xs text-[#FF6A00] font-semibold" data-testid={`cms-strlist-library-${key}`}>+ Add from library</button>
              </div>
            </div>
          );
        }
        return <label key={key} className="block"><span className="text-xs text-[#5C6B61] uppercase">{key}</span><Field type={type} value={block.props?.[key]} onChange={(v) => setProp(key, v)} /></label>;
      })}
      <MediaPicker open={!!galKey} kind="image" onClose={() => setGalKey(null)} onSelect={(url) => setProp(galKey, [...(block.props?.[galKey] || []), url])} />
    </div>
  );
}

export default function AdminPageEditor() {
  const { id } = useParams();
  const [page, setPage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openBlock, setOpenBlock] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [history, setHistory] = useState(null);
  const [sched, setSched] = useState(false);
  const [schedAt, setSchedAt] = useState("");

  const load = useCallback(() => { api.cmsGet(id).then(setPage).catch(() => toast.error("Could not load page")); }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!page) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#FF6A00]" /></div>;

  const sections = page.sections || [];
  const setSections = (updater) => setPage((prev) => ({ ...prev, sections: typeof updater === "function" ? updater(prev.sections || []) : updater }));
  const updateBlock = (i, b) => setSections((s) => s.map((x, j) => j === i ? b : x));
  const move = (i, d) => setSections((s) => { const j = i + d; if (j < 0 || j >= s.length) return s; const a = [...s]; [a[i], a[j]] = [a[j], a[i]]; return a; });
  const addBlock = (type) => { setSections((s) => [...s, { id: uid(), type, props: {}, hidden: false }]); setOpenBlock(sections.length); };

  const save = async () => { setBusy(true); try { const p = await api.cmsUpdate(id, { name: page.name, slug: page.slug, seo_title: page.seo_title, seo_description: page.seo_description, sections }); setPage(p); toast.success("Draft saved"); } catch { toast.error("Save failed"); } finally { setBusy(false); } };
  const publish = async () => { setBusy(true); try { await api.cmsUpdate(id, { name: page.name, slug: page.slug, seo_title: page.seo_title, seo_description: page.seo_description, sections }); const p = await api.cmsPublish(id); setPage(p); toast.success("Published"); } catch { toast.error("Publish failed"); } finally { setBusy(false); } };
  const openHistory = async () => { try { setHistory(await api.cmsRevisions(id)); } catch { toast.error("Could not load history"); } };
  const restoreRev = async (revId) => { if (!window.confirm("Restore this version? Current state is saved first.")) return; try { const p = await api.cmsRestoreRevision(id, revId); setPage(p); setHistory(null); toast.success("Version restored"); } catch { toast.error("Restore failed"); } };
  const scheduleIt = async () => {
    if (!schedAt) { toast.error("Pick a date and time"); return; }
    setBusy(true);
    try {
      await api.cmsUpdate(id, { sections });
      const p = await api.cmsSchedule(id, new Date(schedAt).toISOString());
      setPage(p); setSched(false); toast.success("Publish scheduled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not schedule"); } finally { setBusy(false); }
  };
  const unschedule = async () => { try { const p = await api.cmsUnschedule(id); setPage(p); toast.success("Schedule cancelled"); } catch { toast.error("Failed"); } };

  const devW = { desktop: "100%", tablet: "768px", mobile: "390px" }[device];

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/pages" className="text-xs text-[#5C6B61] inline-flex items-center gap-1 mb-1"><ArrowLeft size={13} /> Pages</Link>
          <div className="flex items-center gap-3"><h1 className={H1}>{page.name}</h1><StatusBadge status={page.status} /></div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openHistory} className={btnSecondary} data-testid="cms-history-btn"><History size={15} /> History</button>
          <button onClick={save} disabled={busy} className={btnSecondary} data-testid="cms-save-btn"><Save size={15} /> Save Draft</button>
          {page.status === "scheduled"
            ? <button onClick={unschedule} disabled={busy} className={btnSecondary} data-testid="cms-unschedule-btn"><Clock size={15} /> Cancel schedule</button>
            : <button onClick={() => setSched(true)} disabled={busy} className={btnSecondary} data-testid="cms-schedule-btn"><Clock size={15} /> Schedule</button>}
          <button onClick={publish} disabled={busy} className={btnPrimary} data-testid="cms-publish-btn"><Globe size={15} /> Publish</button>
        </div>
      </div>

      {page.status === "scheduled" && page.scheduled_at && (
        <div className="mt-4 flex items-center gap-2 bg-[#8A5A44]/10 text-[#8A5A44] px-4 py-2.5 text-sm" data-testid="cms-scheduled-banner">
          <Clock size={15} /> Scheduled to publish on {new Date(page.scheduled_at).toLocaleString()}
        </div>
      )}

      <div className="grid lg:grid-cols-[380px_1fr] gap-6 mt-6">
        {/* EDITOR */}
        <div>
          <p className={OVERLINE}>Page Settings</p>
          <div className="space-y-2 mt-2 mb-6">
            {[["name", "Name"], ["slug", "URL slug"], ["seo_title", "SEO Title"], ["seo_description", "SEO Description"]].map(([k, l]) => (
              <label key={k} className="block"><span className="text-xs text-[#5C6B61]">{l}</span><input value={page[k] || ""} onChange={(e) => setPage({ ...page, [k]: e.target.value })} className={inputCls} data-testid={`cms-meta-${k}`} /></label>
            ))}
          </div>

          <p className={OVERLINE}>Sections</p>
          <div className="space-y-2 mt-2" data-testid="cms-sections">
            {sections.map((b, i) => (
              <div key={b.id} className={`border ${b.hidden ? "border-dashed border-[#1F2A22]/20 opacity-60" : "border-[#1F2A22]/15"} bg-white`} data-testid={`cms-block-${i}`}>
                <div className="flex items-center gap-1 px-3 py-2">
                  <button onClick={() => setOpenBlock(openBlock === i ? null : i)} className="flex-1 text-left font-semibold text-sm text-[#1F2A22]">{BLOCK_LABELS[b.type] || b.type}</button>
                  <button onClick={() => move(i, -1)} title="Up" className="p-1 hover:bg-[#E5E2DC]"><ChevronUp size={15} /></button>
                  <button onClick={() => move(i, 1)} title="Down" className="p-1 hover:bg-[#E5E2DC]"><ChevronDown size={15} /></button>
                  <button onClick={() => updateBlock(i, { ...b, hidden: !b.hidden })} title="Hide/Show" className="p-1 hover:bg-[#E5E2DC]">{b.hidden ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                  <button onClick={() => setSections((s) => [...s.slice(0, i + 1), { ...b, id: uid() }, ...s.slice(i + 1)])} title="Duplicate" className="p-1 hover:bg-[#E5E2DC]"><Copy size={15} /></button>
                  <button onClick={() => setSections((s) => s.filter((_, j) => j !== i))} title="Delete" className="p-1 hover:bg-[#E5E2DC] text-red-600"><Trash2 size={15} /></button>
                </div>
                {openBlock === i && <div className="px-3 pb-3 border-t border-[#1F2A22]/10"><BlockFields block={b} update={(nb) => updateBlock(i, nb)} /></div>}
              </div>
            ))}
          </div>

          <div className="mt-4">
            <span className="text-xs text-[#5C6B61]">Add section</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.keys(SCHEMA).map((t) => <button key={t} onClick={() => addBlock(t)} className="text-xs border border-[#1F2A22]/20 px-2.5 py-1.5 hover:bg-[#1F2A22] hover:text-white" data-testid={`cms-add-${t}`}><Plus size={11} className="inline mr-1" />{BLOCK_LABELS[t]}</button>)}
            </div>
          </div>
        </div>

        {/* LIVE PREVIEW */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className={OVERLINE}>Live Preview</p>
            <div className="flex gap-1">
              {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([d, Ic]) => (
                <button key={d} onClick={() => setDevice(d)} className={`p-2 ${device === d ? "bg-[#1F2A22] text-white" : "hover:bg-[#E5E2DC]"}`} data-testid={`cms-device-${d}`}><Ic size={15} /></button>
              ))}
            </div>
          </div>
          <div className="border border-[#1F2A22]/15 bg-paper overflow-auto" style={{ maxHeight: "70vh" }}>
            <div style={{ width: devW, margin: "0 auto" }} data-testid="cms-preview"><BlockRenderer sections={sections} /></div>
          </div>
        </div>
      </div>

      {history && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setHistory(null)}>
          <div className="bg-[#F9F8F6] w-full max-w-md p-6 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="cms-history-modal">
            <div className="flex items-center justify-between mb-4"><h2 className="font-serif text-2xl text-[#1F2A22]">Version History</h2><button onClick={() => setHistory(null)}><X size={20} /></button></div>
            {history.length === 0 ? <p className="text-[#5C6B61] text-sm">No previous versions yet.</p> : history.map((r) => (
              <div key={r.id} className="flex items-center justify-between border-b border-[#1F2A22]/10 py-3">
                <div><p className="text-sm text-[#1F2A22]">{new Date(r.created_at).toLocaleString()}</p><p className="text-xs text-[#5C6B61]">by {r.admin}</p></div>
                <button onClick={() => restoreRev(r.id)} className={btnSecondary} data-testid="cms-restore-rev">Restore</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {sched && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setSched(false)}>
          <div className="bg-[#F9F8F6] w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="cms-schedule-modal">
            <div className="flex items-center justify-between mb-4"><h2 className="font-serif text-2xl text-[#1F2A22]">Schedule publish</h2><button onClick={() => setSched(false)}><X size={20} /></button></div>
            <p className="text-sm text-[#5C6B61] mb-3">The current draft will go live automatically at the time you choose.</p>
            <input type="datetime-local" value={schedAt} onChange={(e) => setSchedAt(e.target.value)} className={inputCls} data-testid="cms-schedule-input" />
            <div className="flex gap-3 mt-4"><button onClick={scheduleIt} disabled={busy} className={btnPrimary} data-testid="cms-schedule-confirm">{busy ? <Loader2 size={15} className="animate-spin" /> : "Schedule"}</button><button onClick={() => setSched(false)} className={btnSecondary}>Cancel</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
